import type { User, UserRole } from '@/types';

/**
 * ¿Esta organización tiene un EQUIPO, o es una cuenta y nadie más?
 *
 * La pregunta parece obvia y no lo es, y por no hacérsela una organización que
 * llama por teléfono y no tiene un solo ejecutivo veía un ranking de ejecutivos
 * —vacío, o con la cuenta de quien administra arriba de todo— y un selector de
 * «mis leads / todos» que repartía trabajo entre una sola persona. Métricas por
 * persona sobre una organización sin personas no son un gráfico vacío: son la
 * pantalla contándole al cliente un producto que no compró.
 *
 * **LA SEÑAL ES LA GENTE, NO UN INTERRUPTOR.** No hay una llave «tiene equipo»
 * en la configuración, y inventarla habría sido peor: nacería en `false` para
 * todos y habría que acordarse de encenderla cliente por cliente, o nacería en
 * `true` y no diría nada. Lo que sí es un hecho comprobable es el DIRECTORIO:
 * si esta organización dio de alta a alguien que trabaja —un usuario, una
 * cuenta de sólo lectura— entonces hay entre quiénes repartir. Si
 * lo único que hay son las cuentas que administran la organización, no lo hay.
 * (Desde que las etiquetas del cliente son dos, «alguien que trabaja» es un
 * `user` o una cuenta de sólo lectura: la jefatura y el ejecutivo eran la misma
 * persona con dos nombres que el servidor nunca devolvía.)
 *
 * Los roles que NO cuentan están acá y no en la pantalla, por la razón de
 * siempre: la misma pregunta la hacen varias superficies, y una copia se
 * separa. Por lo mismo se exporta: quién administra también decide quién borra
 * la nota de otro (`permisos.ts`).
 */
export const ROLES_QUE_ADMINISTRAN: readonly UserRole[] = ['admin'];

/**
 * Las personas de esta organización que TRABAJAN en ella, más allá de quien la
 * administra. Una cuenta desactivada no cuenta: ya no reparte trabajo con
 * nadie, y dejarla contando mantendría vivo un ranking de gente que se fue.
 */
export function miembrosDelEquipo(users: readonly User[]): User[] {
  return users.filter(
    (user) => user.status !== 'disabled' && !ROLES_QUE_ADMINISTRAN.includes(user.role),
  );
}

/**
 * Si esta organización tiene equipo, y con él sentido las métricas por persona.
 *
 * `undefined` mientras el directorio no llegó: se lee como «todavía no» —o sea
 * NO se dibuja— porque la asimetría es clara. Dibujar un ranking y esconderlo
 * medio segundo después es un parpadeo que se ve; no dibujarlo y que aparezca
 * cuando el dato llega, no.
 */
export function hayEquipo(users?: readonly User[] | null): boolean {
  if (!users) return false;
  return miembrosDelEquipo(users).length > 0;
}
